import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { getErrorMessage } from '../types/api'

interface CreateJobDto {
  name: string
  description: string
  clientName: string
  clientEmail: string
  clientPhone: string
  jobSiteAddress: string
  latitude: number
  longitude: number
  geofenceRadius: number
  startDate: string
  endDate?: string
  estimatedBudget: number
  assignedWorkerIds: string[]
}

interface Worker {
  id: string
  firstName: string
  lastName: string
  email: string
  role: string
}

export function CreateJobPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [formData, setFormData] = useState<CreateJobDto>({
    name: '',
    description: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    jobSiteAddress: '',
    latitude: 0,
    longitude: 0,
    geofenceRadius: 100,
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    estimatedBudget: 0,
    assignedWorkerIds: []
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // All authenticated users can create jobs
  const canCreateJob = !!user

  // Only Admin and ProjectManager can assign workers
  const canAssignWorkers = !!(user && (user.role === 'Admin' || user.role === 'ProjectManager'))

  // Fetch available workers
  const { data: workers = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: async (): Promise<Worker[]> => {
      const response = await apiClient.get('/users/workers')
      return response.data
    },
    enabled: canAssignWorkers
  })

  const createJobMutation = useMutation({
    mutationFn: async (data: CreateJobDto) => {
      // Convert string dates to ISO format for backend
      const payload = {
        ...data,
        startDate: new Date(data.startDate).toISOString(),
        endDate: data.endDate ? new Date(data.endDate).toISOString() : null
      }
      console.log('Sending job data:', payload)
      const response = await apiClient.post('/jobs', payload)
      return response.data
    },
    onSuccess: (data) => {
      console.log('Job created successfully:', data)
      navigate(`/jobs/${data.id}`)
    },
    onError: (error: unknown) => {
      console.error('Job creation error:', error)
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: { data?: { errors?: Record<string, string>, message?: string }, status?: number, headers?: unknown } }
        console.error('API Error Details:', apiError.response?.data)
        console.error('Status Code:', apiError.response?.status)
        console.error('Headers:', apiError.response?.headers)
        if (apiError.response?.data?.errors) {
          setErrors(apiError.response.data.errors)
        } else if (apiError.response?.data?.message) {
          setErrors({ general: apiError.response.data.message })
        }
      }
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    
    // Basic validation
    const newErrors: Record<string, string> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Job name is required'
    }
    if (formData.name.length > 300) {
      newErrors.name = 'Job name must be 300 characters or less'
    }
    if (!formData.clientName.trim()) {
      newErrors.clientName = 'Client name is required'
    }
    if (formData.clientName.length > 200) {
      newErrors.clientName = 'Client name must be 200 characters or less'
    }
    if (!formData.jobSiteAddress.trim()) {
      newErrors.jobSiteAddress = 'Job site address is required'
    }
    if (formData.jobSiteAddress.length > 500) {
      newErrors.jobSiteAddress = 'Job site address must be 500 characters or less'
    }
    if (formData.description.length > 2000) {
      newErrors.description = 'Description must be 2000 characters or less'
    }
    if (formData.clientEmail && formData.clientEmail.length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.clientEmail)) {
        newErrors.clientEmail = 'Please enter a valid email address'
      }
    }
    if (formData.clientPhone.length > 20) {
      newErrors.clientPhone = 'Phone number must be 20 characters or less'
    }
    if (formData.estimatedBudget < 0) {
      newErrors.estimatedBudget = 'Budget must be positive'
    }
    if (formData.geofenceRadius < 10 || formData.geofenceRadius > 1000) {
      newErrors.geofenceRadius = 'Geofence radius must be between 10 and 1000 meters'
    }
    if (formData.latitude < -90 || formData.latitude > 90) {
      newErrors.latitude = 'Latitude must be between -90 and 90'
    }
    if (formData.longitude < -180 || formData.longitude > 180) {
      newErrors.longitude = 'Longitude must be between -180 and 180'
    }
    if (formData.endDate && formData.endDate < formData.startDate) {
      newErrors.endDate = 'End date must be after start date'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    
    createJobMutation.mutate(formData)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }))
  }

  const handleWorkerToggle = (workerId: string) => {
    setFormData(prev => ({
      ...prev,
      assignedWorkerIds: prev.assignedWorkerIds.includes(workerId)
        ? prev.assignedWorkerIds.filter(id => id !== workerId)
        : [...prev.assignedWorkerIds, workerId]
    }))
  }

  const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
      )
      const data = await response.json()
      
      if (data && data.display_name) {
        // Format the address nicely
        const address = data.address
        let formattedAddress = ''
        
        if (address.house_number && address.road) {
          formattedAddress += `${address.house_number} ${address.road}`
        } else if (address.road) {
          formattedAddress += address.road
        }
        
        if (address.city || address.town || address.village) {
          const city = address.city || address.town || address.village
          formattedAddress += formattedAddress ? `, ${city}` : city
        }
        
        if (address.state) {
          formattedAddress += formattedAddress ? `, ${address.state}` : address.state
        }
        
        if (address.postcode) {
          formattedAddress += ` ${address.postcode}`
        }
        
        if (address.country) {
          formattedAddress += formattedAddress ? `, ${address.country}` : address.country
        }
        
        return formattedAddress || data.display_name
      }
      
      return ''
    } catch (error) {
      console.error('Reverse geocoding failed:', error)
      return ''
    }
  }

  const generateJobDataFromLocation = (address: string, geocodeData: { address?: Record<string, string> }) => {
    // Extract useful information from the geocoded address
    const addressData = geocodeData?.address || {}
    
    // Generate job name based on address
    let jobName = ''
    if (addressData.house_number && addressData.road) {
      jobName = `Project - ${addressData.house_number} ${addressData.road}`
    } else if (addressData.road) {
      jobName = `Project - ${addressData.road}`
    } else {
      jobName = `Project - ${address.split(',')[0]}`
    }
    
    // Generate client information based on location context
    const city = addressData.city || addressData.town || addressData.village || 'City'
    
    // Generate a professional client name based on the area
    const clientNames = [
      `${city} Properties LLC`,
      `${addressData.road || 'Main'} Residence Owner`,
      `Property Owner - ${city}`,
      `${city} Development Group`,
      `Homeowner - ${addressData.house_number || ''} ${addressData.road || 'Street'}`.trim()
    ]
    
    const randomClientName = clientNames[Math.floor(Math.random() * clientNames.length)]
    
    // Generate email based on client name
    const emailDomain = city.toLowerCase().replace(/\s+/g, '') + 'properties.com'
    const emailPrefix = randomClientName.toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 15)
    const clientEmail = `${emailPrefix}@${emailDomain}`
    
    // Generate a phone number with area code based on state/region
    const areaCodes = ['555', '123', '456', '789', '321'] // Using test numbers
    const areaCode = areaCodes[Math.floor(Math.random() * areaCodes.length)]
    const clientPhone = `${areaCode}-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 9000 + 1000)}`
    
    // Generate project description
    const projectTypes = [
      'home renovation',
      'property improvement',
      'residential upgrade',
      'maintenance project',
      'repair work',
      'construction project'
    ]
    const randomProjectType = projectTypes[Math.floor(Math.random() * projectTypes.length)]
    const description = `Professional ${randomProjectType} at ${address}. Quality workmanship with attention to detail and customer satisfaction.`
    
    return {
      jobName,
      clientName: randomClientName,
      clientEmail,
      clientPhone,
      description
    }
  }

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = parseFloat(position.coords.latitude.toFixed(6))
          const lng = parseFloat(position.coords.longitude.toFixed(6))
          
          // Update coordinates immediately
          setFormData(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng
          }))
          
          // Try to get the address and generate job data
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
            )
            const geocodeData = await response.json()
            
            if (geocodeData && geocodeData.display_name) {
              // Format the address nicely
              const address = geocodeData.address
              let formattedAddress = ''
              
              if (address.house_number && address.road) {
                formattedAddress += `${address.house_number} ${address.road}`
              } else if (address.road) {
                formattedAddress += address.road
              }
              
              if (address.city || address.town || address.village) {
                const city = address.city || address.town || address.village
                formattedAddress += formattedAddress ? `, ${city}` : city
              }
              
              if (address.state) {
                formattedAddress += formattedAddress ? `, ${address.state}` : address.state
              }
              
              if (address.postcode) {
                formattedAddress += ` ${address.postcode}`
              }
              
              if (address.country) {
                formattedAddress += formattedAddress ? `, ${address.country}` : address.country
              }
              
              const finalAddress = formattedAddress || geocodeData.display_name
              
              // Generate smart job data based on location
              const jobData = generateJobDataFromLocation(finalAddress, geocodeData)
              
              setFormData(prev => ({
                ...prev,
                latitude: lat,
                longitude: lng,
                jobSiteAddress: finalAddress,
                name: jobData.jobName,
                description: jobData.description,
                clientName: jobData.clientName,
                clientEmail: jobData.clientEmail,
                clientPhone: jobData.clientPhone
              }))
            }
          } catch (error) {
            console.error('Failed to get address:', error)
            // Coordinates are already set, just continue without address
          }
        },
        (error) => {
          console.error('Error getting location:', error)
          alert('Could not get your location. Please enter coordinates manually.')
        }
      )
    } else {
      alert('Geolocation is not supported by your browser')
    }
  }

  const selectFromContacts = async () => {
    try {
      // Check if the Contacts API is supported (mainly mobile browsers)
      if ('contacts' in navigator && 'ContactsManager' in window) {
        const navigator_any = navigator as unknown as { contacts: { select: (properties: string[], options: { multiple: boolean }) => Promise<Array<{ name?: string[], email?: string[], tel?: string[] }>> } }
        const contacts = await navigator_any.contacts.select(
          ['name', 'email', 'tel'],
          { multiple: false }
        )
        
        if (contacts && contacts.length > 0) {
          const contact = contacts[0]
          
          setFormData(prev => ({
            ...prev,
            clientName: contact.name?.[0] || '',
            clientEmail: contact.email?.[0] || '',
            clientPhone: contact.tel?.[0] || ''
          }))
          
          return
        }
      }
      
      // Fallback: Try to access local storage contacts (if previously saved)
      const savedContacts = localStorage.getItem('contractorpro_contacts')
      if (savedContacts) {
        const contacts = JSON.parse(savedContacts) as Array<{ name: string, email: string, phone: string }>
        if (contacts.length > 0) {
          // Show a simple contact picker
          showContactPicker(contacts)
          return
        }
      }
      
      // If no contacts available, show manual entry option
      alert('Contact access not available. You can:\n\n1. On mobile: Use a browser that supports contact access\n2. Add contacts manually below\n3. Use the "Save Contact" feature after creating jobs to build a contact list')
      
    } catch (error) {
      console.error('Contact access failed:', error)
      
      // Check if it's a permission issue
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        alert('Contact access denied. Please grant permission to access contacts and try again.')
      } else {
        alert('Contact access not supported on this device/browser. Please enter client information manually.')
      }
    }
  }

  const showContactPicker = (contacts: Array<{ name: string, email: string, phone: string }>) => {
    // Create a simple modal to pick from saved contacts
    const contactList = contacts.map((contact, index) => 
      `${index + 1}. ${contact.name} - ${contact.email} - ${contact.phone}`
    ).join('\n')
    
    const selection = prompt(
      `Select a contact by number:\n\n${contactList}\n\nEnter the contact number:`
    )
    
    const contactIndex = parseInt(selection || '0') - 1
    if (contactIndex >= 0 && contactIndex < contacts.length) {
      const selectedContact = contacts[contactIndex]
      setFormData(prev => ({
        ...prev,
        clientName: selectedContact.name,
        clientEmail: selectedContact.email,
        clientPhone: selectedContact.phone
      }))
    }
  }

  const saveContactForFuture = () => {
    const { clientName, clientEmail, clientPhone } = formData
    
    if (!clientName.trim()) {
      alert('Please enter a client name first')
      return
    }
    
    const newContact = {
      name: clientName,
      email: clientEmail,
      phone: clientPhone,
      dateAdded: new Date().toISOString()
    }
    
    // Get existing contacts
    const existingContacts = JSON.parse(localStorage.getItem('contractorpro_contacts') || '[]') as Array<{ name: string, email: string, phone: string }>
    
    // Check if contact already exists
    const existingIndex = existingContacts.findIndex((c) => 
      c.name.toLowerCase() === clientName.toLowerCase() || 
      (clientEmail && c.email.toLowerCase() === clientEmail.toLowerCase())
    )
    
    if (existingIndex >= 0) {
      // Update existing contact
      existingContacts[existingIndex] = newContact
      alert('Contact updated in your saved contacts!')
    } else {
      // Add new contact
      existingContacts.push(newContact)
      alert('Contact saved for future use!')
    }
    
    // Save back to localStorage
    localStorage.setItem('contractorpro_contacts', JSON.stringify(existingContacts))
  }

  const loadTestData = async () => {
    const jobTypes = [
      'Kitchen Renovation',
      'Bathroom Remodel',
      'Deck Construction',
      'Roof Repair',
      'Foundation Work',
      'Electrical Upgrade',
      'Plumbing Installation',
      'Flooring Installation',
      'Garage Addition',
      'Basement Finishing'
    ]
    
    const clients = [
      { name: 'John Smith', email: 'john.smith@email.com', phone: '555-0123' },
      { name: 'Sarah Johnson', email: 'sarah.j@email.com', phone: '555-0456' },
      { name: 'Mike Wilson', email: 'mike.wilson@email.com', phone: '555-0789' },
      { name: 'Lisa Brown', email: 'lisa.brown@email.com', phone: '555-0321' },
      { name: 'David Jones', email: 'david.jones@email.com', phone: '555-0654' },
      { name: 'Emily Davis', email: 'emily.davis@email.com', phone: '555-0987' }
    ]
    
    const descriptions = [
      'Complete renovation with modern fixtures and appliances',
      'Full remodel including structural changes and new design',
      'High-quality construction with premium materials',
      'Repair and upgrade to meet current building codes',
      'Custom installation with attention to detail',
      'Professional work with warranty included'
    ]

    // Generate random data
    const randomJobType = jobTypes[Math.floor(Math.random() * jobTypes.length)]
    const randomClient = clients[Math.floor(Math.random() * clients.length)]
    const randomDescription = descriptions[Math.floor(Math.random() * descriptions.length)]
    
    // Generate random coordinates (somewhere in continental US)
    const lat = parseFloat((Math.random() * (49 - 25) + 25).toFixed(6)) // 25°N to 49°N
    const lng = parseFloat((Math.random() * (-66 - (-125)) + (-125)).toFixed(6)) // -125°W to -66°W
    
    // Generate random dates
    const startDate = new Date()
    startDate.setDate(startDate.getDate() + Math.floor(Math.random() * 30)) // 0-30 days from now
    
    const endDate = new Date(startDate)
    endDate.setDate(endDate.getDate() + Math.floor(Math.random() * 90) + 7) // 7-97 days after start
    
    // Generate random budget between $5,000 and $150,000
    const budget = Math.floor(Math.random() * 145000) + 5000

    // Try to get real address from coordinates
    let jobSiteAddress = 'Address lookup in progress...'
    try {
      const realAddress = await reverseGeocode(lat, lng)
      if (realAddress) {
        jobSiteAddress = realAddress
      } else {
        // Fallback to a generic address if geocoding fails
        jobSiteAddress = `${Math.floor(Math.random() * 9999) + 1} Main Street, Sample City, USA`
      }
    } catch (error) {
      console.error('Failed to get address for test data:', error)
      jobSiteAddress = `${Math.floor(Math.random() * 9999) + 1} Main Street, Sample City, USA`
    }

    const testData: CreateJobDto = {
      name: `${randomJobType} - ${jobSiteAddress.split(',')[0]}`,
      description: `${randomJobType.toLowerCase()} project. ${randomDescription}`,
      clientName: randomClient.name,
      clientEmail: randomClient.email,
      clientPhone: randomClient.phone,
      jobSiteAddress: jobSiteAddress,
      latitude: lat,
      longitude: lng,
      geofenceRadius: Math.floor(Math.random() * 200) + 50, // 50-250 meters
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      estimatedBudget: budget,
      assignedWorkerIds: [] // Don't auto-assign workers for test data
    }

    setFormData(testData)
    setErrors({}) // Clear any existing errors
  }

  if (!canCreateJob) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="md:flex md:items-center md:justify-between mb-8">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
            Create New Job
          </h1>
          <div className="mt-4 md:mt-0">
            <button
              type="button"
              onClick={loadTestData}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Load Test Data
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6">
          {/* Job Information */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Job Information</h2>
            
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Job Name *
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Kitchen Renovation - 123 Main St"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
                    errors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Complete kitchen renovation including cabinets, countertops, and appliances..."
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    id="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    id="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
                      errors.endDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.endDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="estimatedBudget" className="block text-sm font-medium text-gray-700">
                  Estimated Budget ($)
                </label>
                <input
                  type="number"
                  name="estimatedBudget"
                  id="estimatedBudget"
                  value={formData.estimatedBudget}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
                    errors.estimatedBudget ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="50000.00"
                />
                {errors.estimatedBudget && (
                  <p className="mt-1 text-sm text-red-600">{errors.estimatedBudget}</p>
                )}
              </div>
            </div>
          </div>

          {/* Client Information */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Client Information</h2>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={selectFromContacts}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                  Select Contact
                </button>
                <button
                  type="button"
                  onClick={saveContactForFuture}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Save Contact
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="clientName" className="block text-sm font-medium text-gray-700">
                  Client Name *
                </label>
                <input
                  type="text"
                  name="clientName"
                  id="clientName"
                  value={formData.clientName}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
                    errors.clientName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="John Smith"
                />
                {errors.clientName && (
                  <p className="mt-1 text-sm text-red-600">{errors.clientName}</p>
                )}
              </div>

              <div>
                <label htmlFor="clientEmail" className="block text-sm font-medium text-gray-700">
                  Client Email
                </label>
                <input
                  type="email"
                  name="clientEmail"
                  id="clientEmail"
                  value={formData.clientEmail}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
                    errors.clientEmail ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="john.smith@example.com"
                />
                {errors.clientEmail && (
                  <p className="mt-1 text-sm text-red-600">{errors.clientEmail}</p>
                )}
              </div>

              <div>
                <label htmlFor="clientPhone" className="block text-sm font-medium text-gray-700">
                  Client Phone
                </label>
                <input
                  type="tel"
                  name="clientPhone"
                  id="clientPhone"
                  value={formData.clientPhone}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
                    errors.clientPhone ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="555-0123"
                />
                {errors.clientPhone && (
                  <p className="mt-1 text-sm text-red-600">{errors.clientPhone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Location Information</h2>
            
            <div className="space-y-6">
              <div>
                <label htmlFor="jobSiteAddress" className="block text-sm font-medium text-gray-700">
                  Job Site Address *
                </label>
                <input
                  type="text"
                  name="jobSiteAddress"
                  id="jobSiteAddress"
                  value={formData.jobSiteAddress}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
                    errors.jobSiteAddress ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="123 Main Street, Anytown, USA 12345"
                />
                {errors.jobSiteAddress && (
                  <p className="mt-1 text-sm text-red-600">{errors.jobSiteAddress}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="latitude" className="block text-sm font-medium text-gray-700">
                    Latitude
                  </label>
                  <input
                    type="number"
                    name="latitude"
                    id="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    step="any"
                    min="-90"
                    max="90"
                    className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
                      errors.latitude ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.latitude && (
                    <p className="mt-1 text-sm text-red-600">{errors.latitude}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="longitude" className="block text-sm font-medium text-gray-700">
                    Longitude
                  </label>
                  <input
                    type="number"
                    name="longitude"
                    id="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    step="any"
                    min="-180"
                    max="180"
                    className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
                      errors.longitude ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.longitude && (
                    <p className="mt-1 text-sm text-red-600">{errors.longitude}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="geofenceRadius" className="block text-sm font-medium text-gray-700">
                    Geofence Radius (m)
                  </label>
                  <input
                    type="number"
                    name="geofenceRadius"
                    id="geofenceRadius"
                    value={formData.geofenceRadius}
                    onChange={handleChange}
                    min="10"
                    max="1000"
                    className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
                      errors.geofenceRadius ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.geofenceRadius && (
                    <p className="mt-1 text-sm text-red-600">{errors.geofenceRadius}</p>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={getCurrentLocation}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Use Current Location
              </button>
            </div>
          </div>

          {/* Assigned Workers */}
          {canAssignWorkers && workers.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Assign Workers</h2>
              
              <div className="space-y-3">
                {workers.map((worker) => (
                  <label key={worker.id} className="flex items-start">
                    <input
                      type="checkbox"
                      checked={formData.assignedWorkerIds.includes(worker.id)}
                      onChange={() => handleWorkerToggle(worker.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900">
                        {worker.firstName} {worker.lastName}
                      </span>
                      <span className="text-sm text-gray-500 ml-2">
                        ({worker.email})
                      </span>
                      <span className="text-xs text-gray-400 ml-2">
                        {worker.role}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Error Message */}
          {(createJobMutation.isError || errors.general) && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {errors.general || getErrorMessage(createJobMutation.error, 'Failed to create job')}
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/jobs')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createJobMutation.isPending}
              className="inline-flex justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createJobMutation.isPending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Job'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}